import { useEffect, useState } from "react";
import { ArrowUpRight, MessageSquarePlus, Star } from "lucide-react";
import { Eyebrow, Reveal } from "@/components/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MAPS_URL = "https://maps.app.goo.gl/jzYk1XyE1DzvGwFo6";

const FALLBACK_REVIEWS = [
  {
    name: "Jon B",
    place: "Google Local Guide",
    rating: 5,
    quote:
      "The kilted courier guy was super friendly and knowledgeable, he helped us move some goods. The whole process was pain-free and affordable, highly recommend him, he can handle our loads anytime.",
  },
  {
    name: "Shaun",
    place: "Google Local Guide",
    rating: 5,
    quote:
      "Dave from Kilted Couriers handled our packages with care, delivering from Cooma to Jindabyne not only fast but at a good price. Thanks Dave, will recommend to others.",
  },
  {
    name: "Jack Searle",
    place: "Owner, Cooma Trophy Store",
    rating: 5,
    quote:
      "Highly recommend Dave, very punctual and professional service! My new go-to courier service for the Snowy Monaro region!",
  },
  {
    name: "Steve",
    place: "Google review",
    rating: 5,
    quote:
      "The guy in the kilt was really friendly and easy to deal with. Delivery was quick and handled with care. Would highly recommend!",
  },
  {
    name: "Colvie Channel",
    place: "Google Local Guide",
    rating: 5,
    quote:
      "Dave has always been on time and handles my package with great care, would recommend over any other package handlers in the area!",
  },
];

const initialsOf = (name) =>
  name
    .split(" ")
    .map((w) => w[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();

export default function Testimonials() {
  const [data, setData] = useState({ rating: 5.0, count: 6, url: MAPS_URL, reviews: FALLBACK_REVIEWS });

  useEffect(() => {
    fetch(`${API}/reviews`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        if (d && Array.isArray(d.reviews) && d.reviews.length) setData(d);
      })
      .catch(() => {});
  }, []);

  const mapsUrl = data.url || MAPS_URL;

  return (
    <section id="reviews" data-testid="reviews-section" className="relative py-24 sm:py-32">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <div className="flex flex-col gap-6 md:flex-row md:items-end md:justify-between">
          <Reveal>
            <Eyebrow>Word on the mountain</Eyebrow>
            <h2 className="font-display mt-4 text-4xl font-extrabold uppercase leading-[0.95] tracking-tight text-[#F6F1E3] sm:text-6xl">
              Trusted from the capital<br />
              <span className="text-outline-gold">to the snowfields</span>
            </h2>
          </Reveal>
          <Reveal delay={0.15}>
            <a
              data-testid="reviews-google-link"
              href={mapsUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="group inline-flex items-center gap-3 rounded-2xl border border-[#F0A829]/40 bg-[#122A1F]/70 px-5 py-4 transition-colors duration-300 hover:border-[#F0A829]"
            >
              <span className="flex gap-0.5" aria-label={`${data.rating} out of 5 stars on Google`}>
                {[0, 1, 2, 3, 4].map((s) => (
                  <Star key={s} className="h-4 w-4 fill-[#F0A829] text-[#F0A829]" />
                ))}
              </span>
              <span className="font-display text-lg font-bold uppercase tracking-wide text-[#F6F1E3]">
                {Number(data.rating).toFixed(1)} on Google
              </span>
              <span className="font-mono-brand text-[10px] uppercase tracking-[0.2em] text-[#B9C4B4]">
                {data.count} reviews
              </span>
              <ArrowUpRight className="h-4 w-4 text-[#F0A829] transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
            </a>
          </Reveal>
        </div>

        <div className="mt-14 grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
          {data.reviews.map((q, i) => (
            <Reveal key={`${q.name}-${i}`} delay={Math.min(i, 5) * 0.08}>
              <blockquote
                data-testid={`review-card-${i}`}
                className="bento-card flex h-full flex-col justify-between rounded-2xl border border-white/10 bg-[#122A1F]/70 p-7"
              >
                <div>
                  <div className="flex gap-1" aria-label={`${q.rating} star review`}>
                    {Array.from({ length: Math.max(1, Math.min(5, q.rating || 5)) }).map((_, s) => (
                      <Star key={s} className="h-4 w-4 fill-[#F0A829] text-[#F0A829]" />
                    ))}
                  </div>
                  <p className="mt-5 text-base leading-relaxed text-[#F6F1E3]/90">
                    &ldquo;{q.quote}&rdquo;
                  </p>
                </div>
                <footer className="mt-8 flex items-center gap-4">
                  <span className="font-display flex h-11 w-11 items-center justify-center rounded-full border border-[#F0A829]/50 bg-[#0C1F17] text-sm font-bold text-[#F0A829]">
                    {initialsOf(q.name)}
                  </span>
                  <div>
                    <p className="font-display text-base font-bold uppercase tracking-wide text-[#F6F1E3]">{q.name}</p>
                    <p className="font-mono-brand text-[10px] uppercase tracking-[0.25em] text-[#B9C4B4]">{q.place}</p>
                  </div>
                </footer>
              </blockquote>
            </Reveal>
          ))}

          <Reveal delay={0.4}>
            <a
              data-testid="review-cta-card"
              href={mapsUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="group flex h-full min-h-[220px] flex-col items-center justify-center gap-4 rounded-2xl border border-dashed border-[#F0A829]/50 bg-transparent p-7 text-center transition-colors duration-300 hover:border-[#F0A829] hover:bg-[#F0A829]/5"
            >
              <span className="flex h-12 w-12 items-center justify-center rounded-full border border-[#F0A829]/50 bg-[#0C1F17]">
                <MessageSquarePlus className="h-5 w-5 text-[#F0A829]" strokeWidth={2} />
              </span>
              <p className="font-display text-2xl font-bold uppercase tracking-wide text-[#F6F1E3]">
                Delivered for you?
              </p>
              <p className="font-mono-brand flex items-center gap-2 text-[11px] uppercase tracking-[0.25em] text-[#F0A829]">
                Leave a Google review
                <ArrowUpRight className="h-3.5 w-3.5 transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
              </p>
            </a>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
